/**
 * PokerGame — Main container component for Texas Hold'em Poker
 *
 * Orchestrates mode selection → engine initialization → hand flow →
 * AI turns → showdown → next hand. Wraps in ErrorBoundary.
 *
 * Requirements: 9.6, 9.7, 11.3, 12.1, 17.8, 22.5, 23.1, 23.3, 23.4, 23.7, 33.1
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PokerEngine } from '../PokerEngine';
import { PokerAI } from '../PokerAI';
import { PokerTable } from './PokerTable';
import { BettingControls } from './BettingControls';
import { HandHistory } from './HandHistory';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { PokerCoach } from './PokerCoach';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { GameSessionManager } from '../../services/GameSessionManager';
import { MultiplayerPokerService } from '../services/MultiplayerPokerService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  PokerGameProps,
  PokerGameSettings,
  PokerDifficulty,
  PlayerAction,
  VisibleGameState,
  VisiblePlayerState,
  ValidAction,
  CommunityCardHistory,
  Card,
  PlayerConfig,
  DEFAULT_BLIND_LEVEL,
  DEFAULT_BUY_IN,
} from '../types';
import './PokerGame.css';

const AI_NAMES = ['Alice', 'Bob', 'Charlie', 'Diana', 'Eve', 'Frank', 'Grace'];

function buildVisibleState(engine: PokerEngine, playerId: string): VisibleGameState {
  return engine.getVisibleState(playerId);
}

function buildCommunityCardHistory(communityCards: Card[]): CommunityCardHistory {
  return {
    flop: communityCards.slice(0, 3),
    turn: communityCards.length >= 4 ? communityCards[3] : null,
    river: communityCards.length >= 5 ? communityCards[4] : null,
  };
}

// ============================================================================
// Inner component (unwrapped from ErrorBoundary)
// ============================================================================

const PokerGameInner: React.FC<PokerGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();

  // Core game state
  const [engine] = useState<PokerEngine>(() => new PokerEngine());
  const [gameMode, setGameMode] = useState<'singleplayer' | 'multiplayer-realtime' | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<PokerDifficulty>('medium');
  const [aiInstances, setAiInstances] = useState<Map<string, PokerAI>>(new Map());
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [moveCount, setMoveCount] = useState(0);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [humanPlayerId] = useState('human-player');

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerPokerService>(
    () => new MultiplayerPokerService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');

  // Refs
  const aiMoveScheduled = useRef(false);
  const gameSessionId = useRef<string | null>(null);
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  let visibleState: VisibleGameState | null = null;
  let validActions: ValidAction[] = [];
  let isGameOver = false;
  let isHandComplete = false;

  try {
    visibleState = buildVisibleState(engine, humanPlayerId);
    validActions = engine.getValidActions(humanPlayerId);
    isGameOver = visibleState.gameStatus.type === 'complete';
    isHandComplete = visibleState.handPhase === 'complete';
  } catch {
    // Engine not initialized yet
  }

  const communityCardHistory = visibleState
    ? buildCommunityCardHistory(visibleState.communityCards)
    : { flop: [], turn: null, river: null };

  const totalPot = visibleState
    ? visibleState.pots.reduce((sum, p) => sum + p.amount, 0) +
      visibleState.players.reduce((sum, p) => sum + p.currentBet, 0)
    : 0;

  const humanPlayer = visibleState?.players.find((p) => p.id === humanPlayerId);
  const callAction = validActions.find((a) => a.type === 'call');
  const raiseAction = validActions.find((a) => a.type === 'raise');

  // -----------------------------------------------------------------------
  // Multiplayer event handlers setup
  // -----------------------------------------------------------------------
  useEffect(() => {
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    });

    multiplayerService.onGameOver((result: GameResult) => {
      const message = result.result === 'win'
        ? `🎉 ${result.winner} wins!`
        : 'Game over!';
      NotificationService.getInstance().sendInfoNotification(message);
    });

    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    multiplayerService.onGameStateReceived((gameState: any) => {
      // Game state sync from server handled here
      setMoveCount((c) => c + 1);
    });

    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    multiplayerService.onReconnectionFailed(() => {
      NotificationService.getInstance().sendErrorNotification(
        'Unable to reconnect. Please return to the menu and try again.'
      );
    });
  }, [multiplayerService]);

  // -----------------------------------------------------------------------
  // Deep link room code handling
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;
      setGameMode('multiplayer-realtime');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);
      setShowModeSelector(false);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setShowModeSelector(true);
          setWaitingForOpponent(false);
          setRoomCode(null);
          setGameMode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // AI turn processing
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!visibleState || gameMode !== 'singleplayer' || isAIThinking || isGameOver || isHandComplete) {
      return;
    }

    const currentPlayer = engine.getCurrentPlayer();
    if (!currentPlayer || currentPlayer === humanPlayerId) {
      return;
    }

    // It's an AI player's turn
    if (!aiMoveScheduled.current) {
      aiMoveScheduled.current = true;
      setIsAIThinking(true);

      const processAITurn = async () => {
        try {
          const aiInstance = aiInstances.get(currentPlayer);
          if (!aiInstance) {
            console.error('No AI instance for player:', currentPlayer);
            return;
          }

          const aiVisibleState = buildVisibleState(engine, currentPlayer);
          const aiValidActions = engine.getValidActions(currentPlayer);
          const action = await aiInstance.getAction(aiVisibleState, currentPlayer, aiValidActions);

          engine.submitAction(currentPlayer, action);
          setMoveCount((c) => c + 1);
        } catch (error) {
          console.error('PokerGame: Error during AI turn:', error);
        } finally {
          setIsAIThinking(false);
          aiMoveScheduled.current = false;
        }
      };

      processAITurn();
    }
  }, [moveCount, visibleState, gameMode, isAIThinking, isGameOver, isHandComplete, engine, humanPlayerId, aiInstances]);

  // -----------------------------------------------------------------------
  // Auto-start next hand when current hand completes
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!visibleState || !isHandComplete || isGameOver || gameMode !== 'singleplayer') {
      return;
    }

    // Check if game should end (only 1 player with chips)
    const playersWithChips = visibleState.players.filter(
      (p) => p.status !== 'eliminated'
    );
    if (playersWithChips.length < 2) {
      return; // Game over will be handled by engine
    }

    // Auto-start next hand after a delay
    const timer = setTimeout(() => {
      try {
        engine.startNewHand();
        setMoveCount((c) => c + 1);
      } catch (error) {
        console.error('Failed to start new hand:', error);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isHandComplete, isGameOver, visibleState, engine, gameMode]);

  // -----------------------------------------------------------------------
  // Show result overlay when game ends
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (isGameOver && !showResultOverlay) {
      const timer = setTimeout(() => setShowResultOverlay(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isGameOver, showResultOverlay]);

  // -----------------------------------------------------------------------
  // Game mode handlers
  // -----------------------------------------------------------------------

  const handleStartGame = useCallback(
    (settings: PokerGameSettings) => {
      if (settings.mode === 'singleplayer') {
        const difficulty = settings.aiDifficulty || 'medium';
        const opponentCount = settings.aiOpponentCount || 3;

        setAiDifficulty(difficulty);

        const players: PlayerConfig[] = [
          { id: humanPlayerId, name: 'You', isHuman: true },
        ];

        const newAiInstances = new Map<string, PokerAI>();

        for (let i = 0; i < opponentCount; i++) {
          const aiId = `ai-${i}`;
          players.push({
            id: aiId,
            name: AI_NAMES[i] || `AI ${i + 1}`,
            isHuman: false,
            aiDifficulty: difficulty,
          });
          const ai = new PokerAI(difficulty);
          ai.testMode = false;
          newAiInstances.set(aiId, ai);
        }

        setAiInstances(newAiInstances);

        engine.initializeGame({
          tableSize: players.length,
          buyIn: DEFAULT_BUY_IN,
          blindLevel: DEFAULT_BLIND_LEVEL,
          players,
        });

        engine.startNewHand();
        setGameMode('singleplayer');
        setShowModeSelector(false);
        setMoveCount(0);
        setIsAIThinking(false);
        setShowResultOverlay(false);
        aiMoveScheduled.current = false;

        try {
          const sessionId = GameSessionManager.getInstance().startGameSession('poker');
          gameSessionId.current = sessionId;
        } catch {
          // Non-critical
        }
      } else {
        // Multiplayer — create room
        if (isCreatingRoom.current) return;
        isCreatingRoom.current = true;

        multiplayerService.createRoom()
          .then((room) => {
            setRoomCode(room.code);
            setWaitingForOpponent(true);
            setGameMode('multiplayer-realtime');
            setShowModeSelector(false);
            isCreatingRoom.current = false;
          })
          .catch((error: any) => {
            NotificationService.getInstance().sendErrorNotification(
              `Failed to create room: ${error.message || 'Unknown error'}`
            );
            isCreatingRoom.current = false;
          });
      }
    },
    [engine, humanPlayerId]
  );

  const handleJoinPrivateGame = useCallback(async (roomCode: string) => {
    setGameMode('multiplayer-realtime');
    setRoomCode(roomCode);
    setWaitingForOpponent(true);
    setShowModeSelector(false);

    try {
      await multiplayerService.joinRoom(roomCode);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${roomCode}. ${error.message || 'Please check the room code.'}`
      );
      setShowModeSelector(true);
      setWaitingForOpponent(false);
      setRoomCode(null);
      setGameMode(null);
    }
  }, [multiplayerService]);

  // -----------------------------------------------------------------------
  // Player action handler
  // -----------------------------------------------------------------------

  const handlePlayerAction = useCallback(
    (action: PlayerAction) => {
      if (isAIThinking || isGameOver) return;

      const currentPlayer = engine.getCurrentPlayer();
      if (currentPlayer !== humanPlayerId) return;

      const result = engine.submitAction(humanPlayerId, action);
      if (result.success) {
        setMoveCount((c) => c + 1);

        if (result.handComplete && gameSessionId.current) {
          // Check if game is complete
          try {
            const state = buildVisibleState(engine, humanPlayerId);
            if (state.gameStatus.type === 'complete') {
              GameSessionManager.getInstance().endGameSession(
                gameSessionId.current,
                true
              );
              gameSessionId.current = null;
            }
          } catch {
            // Non-critical
          }
        }
      }
    },
    [engine, humanPlayerId, isAIThinking, isGameOver]
  );

  // -----------------------------------------------------------------------
  // New game / exit
  // -----------------------------------------------------------------------

  const handleNewGame = useCallback(() => {
    if (roomCode) {
      multiplayerService.leaveGame();
    }
    setGameMode(null);
    setShowModeSelector(true);
    setMoveCount(0);
    setIsAIThinking(false);
    setShowResultOverlay(false);
    aiMoveScheduled.current = false;
    setAiInstances(new Map());
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setConnectionStatus('connected');
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  // -----------------------------------------------------------------------
  // Display helpers
  // -----------------------------------------------------------------------

  const getTurnText = (): string => {
    if (!visibleState) return '';

    if (isGameOver) {
      const winner = visibleState.players.find(
        (p) => p.status !== 'eliminated'
      );
      if (winner?.id === humanPlayerId) return '🎉 You Win the Game!';
      return `😔 ${winner?.name || 'Opponent'} Wins the Game!`;
    }

    if (isHandComplete) {
      return 'Hand complete — next hand starting...';
    }

    if (visibleState.handPhase === 'showdown') {
      return 'Showdown!';
    }

    if (isAIThinking) return '🤔 AI is thinking...';

    const currentPlayer = engine.getCurrentPlayer();
    if (currentPlayer === humanPlayerId) return '🎯 Your Turn — Choose an action';
    const aiPlayer = visibleState.players.find((p) => p.id === currentPlayer);
    return `Waiting for ${aiPlayer?.name || 'opponent'}...`;
  };

  const getHeaderTitle = (): string => {
    if (gameMode === 'singleplayer') {
      const diffLabel = aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1);
      return `Poker vs AI — ${diffLabel}`;
    }
    if (gameMode === 'multiplayer-realtime') return 'Poker — Online';
    return 'Poker';
  };

  // -----------------------------------------------------------------------
  // Render: Mode selector
  // -----------------------------------------------------------------------

  if (showModeSelector) {
    const pokerConfig: GameModeConfig = {
      gameName: 'Poker',
      gameIcon: '🃏',
      subtitle: 'Texas Hold\'em',
      availableModes: ['ai', 'online'],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai') {
        const difficultyMap: Record<string, PokerDifficulty> = { easy: 'easy', medium: 'medium', hard: 'hard' };
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: difficultyMap[selection.difficulty || 'medium'] || 'medium',
          aiOpponentCount: 3,
        });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="poker-game-container">
        <UniversalGameModeSelector
          config={pokerConfig}
          onStart={handleModeSelection}
          onJoinRoom={handleJoinPrivateGame}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime' })}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={
            isAuthenticated
              ? undefined
              : GameSessionManager.getInstance().getRemainingFreeGames()
          }
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Waiting for opponent
  // -----------------------------------------------------------------------

  if (waitingForOpponent && roomCode) {
    return (
      <div className="poker-game-container">
        <RoomCodeDisplay
          roomCode={roomCode}
          gameType="poker"
          playerName="Guest"
          maxPlayers={8}
          onCancel={() => {
            multiplayerService.leaveGame();
            setWaitingForOpponent(false);
            setRoomCode(null);
            setShowModeSelector(true);
            setGameMode(null);
            isCreatingRoom.current = false;
          }}
          showCancelButton={true}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Game in progress
  // -----------------------------------------------------------------------

  const gameOverConfig = showResultOverlay && isGameOver && visibleState ? {
    show: true,
    result: (visibleState.players.find((p) => p.status !== 'eliminated')?.id === humanPlayerId ? 'win' : 'loss') as 'win' | 'loss',
    title: visibleState.players.find((p) => p.status !== 'eliminated')?.id === humanPlayerId ? 'Victory!' : 'Game Over',
    subtitle: visibleState.players.find((p) => p.status !== 'eliminated')?.id === humanPlayerId
      ? 'You won the poker game!'
      : `${visibleState.players.find((p) => p.status !== 'eliminated')?.name || 'Opponent'} wins!`,
    icon: visibleState.players.find((p) => p.status !== 'eliminated')?.id === humanPlayerId ? '🏆' : '💔',
    onPlayAgain: handleNewGame,
    onExit: onExit || handleNewGame,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Poker',
    board: visibleState ? (
      <div className="poker-game-layout__table">
        <PokerTable
          engine={engine}
          gameState={visibleState}
          currentPlayerId={humanPlayerId}
          onAction={handlePlayerAction}
          isMultiplayer={gameMode === 'multiplayer-realtime'}
        />
      </div>
    ) : undefined,
    leftPanels: visibleState ? [
      {
        id: 'betting',
        title: 'Betting Controls',
        icon: '💰',
        content: (
          <BettingControls
            validActions={validActions}
            pot={totalPot}
            callAmount={callAction?.callAmount || 0}
            minRaise={raiseAction?.minAmount || 0}
            maxRaise={raiseAction?.maxAmount || humanPlayer?.chipStack || 0}
            playerStack={humanPlayer?.chipStack || 0}
            onAction={handlePlayerAction}
            disabled={
              isAIThinking ||
              isGameOver ||
              isHandComplete ||
              engine.getCurrentPlayer() !== humanPlayerId
            }
          />
        ),
      },
      {
        id: 'history',
        title: 'Hand History',
        icon: '📜',
        content: (
          <HandHistory
            actions={visibleState.actionHistory}
            communityCards={communityCardHistory}
            currentPhase={visibleState.handPhase}
          />
        ),
      },
    ] : [],
    rightPanels: gameMode === 'singleplayer' && visibleState ? [
      {
        id: 'coach',
        title: 'Poker Coach',
        icon: '🧠',
        content: (
          <PokerCoach
            gameState={visibleState}
            playerId={humanPlayerId}
            validActions={validActions}
            isPlayerTurn={engine.getCurrentPlayer() === humanPlayerId && !isAIThinking && !isGameOver && !isHandComplete}
          />
        ),
      },
    ] : undefined,
    statusBar: {
      turnIndicator: <span>{getTurnText()}</span>,
      gameStatus: isGameOver ? 'Game Over' : undefined,
      gameStatusType: isGameOver ? 'ended' : 'active',
      aiThinking: isAIThinking,
      connectionStatus: gameMode === 'multiplayer-realtime' ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
    ],
    gameOver: gameOverConfig,
    roomCode: roomCode || undefined,
  };

  return (
    <div className="poker-game-container" role="main" aria-label="Poker Game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

// ============================================================================
// Exported component wrapped in ErrorBoundary
// ============================================================================

export const PokerGame: React.FC<PokerGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="Poker">
      <PokerGameInner {...props} />
    </ErrorBoundary>
  );
};

export default PokerGame;
